import { PlusIcon } from '@shopify/polaris-icons';
import { Page, TextField, Modal } from '../components/forms';
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  ChoiceList,
  DataTable,
  Checkbox,
  Spinner,
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import { useRouteSave, localRouteDate } from '../components/routes/useRouteSave';

interface AvailableOrder {
  id: number;
  order_number: string;
  customer_name: string;
  shipping_address_line1: string;
  shipping_postcode: string;
  delivery_date: string;
  total_price: number;
  currency: string | null;
  zone_name: string | null;
}

interface Zone {
  id: number;
  name: string;
}

interface DeliveryRoute {
  id: number;
  name: string | null;
  delivery_date: string;
  total_stops: number;
  revision: number;
  status: string;
  zone_ids: number[];
}

export default function RouteBuilder() { const { currentShop } = useAuth(); const [params] = useSearchParams(); return <Builder key={`${currentShop?.id}-${params.get('route')}`} />; }
function Builder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const existingRouteId = searchParams.get('route');

  const { fetchApi, error, loading } = useApi(); const { hasPermission } = useAuth();
  const generation = useRef(0); const routeGeneration = useRef(0); const zoneGeneration = useRef(0);
  const [truncated, setTruncated] = useState(false);
  const [orders, setOrders] = useState<AvailableOrder[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());

  // Filters
  const [selectedDate, setSelectedDate] = useState(() => {
    return localRouteDate();
  });
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  // Create route modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newStartTime, setNewStartTime] = useState('08:00');
  const mutation = useRouteSave(); const creating = mutation.saving;

  // Existing route
  const [existingRoute, setExistingRoute] = useState<DeliveryRoute | null>(null);
  const addingToRoute = mutation.saving;

  const loadOrders = useCallback(async () => {
    const version = ++generation.current;
    setSelectedOrders(new Set());
    const params = new URLSearchParams();

    if (selectedDate) params.append('date', selectedDate);
    if (existingRouteId) params.append('route_id', existingRouteId);
    else if (selectedZones.length) params.append('zone_ids', selectedZones.join(','));

    const data = await fetchApi<{ orders: AvailableOrder[]; truncated: boolean }>(`/routes/available-orders?${params.toString()}`);

    if (version !== generation.current) return;
    setOrders(data?.orders || []); setTruncated(data?.truncated || false);
  }, [fetchApi, selectedDate, selectedZones, existingRouteId]);

  const loadZones = useCallback(async () => {
    const version = ++zoneGeneration.current;
    const data = await fetchApi<{ routes: never[]; zones: Zone[] }>('/routes?limit=0');
    if (version !== zoneGeneration.current) return;
    if (data) {
      setZones(data.zones || []);
    }
  }, [fetchApi]);

  const loadExistingRoute = useCallback(async () => {
    if (!existingRouteId) return;
    const version = ++routeGeneration.current;

    const data = await fetchApi<{ route: DeliveryRoute }>(`/routes/${existingRouteId}`);
    if (version !== routeGeneration.current) return;
    if (!data) setExistingRoute(null);
    if (data) {
      setExistingRoute(data.route);
      setSelectedZones(data.route.zone_ids.map(String));
      // Set date filter to match route
      if (data.route.delivery_date) {
        setSelectedDate(data.route.delivery_date);
      }
    }
  }, [fetchApi, existingRouteId]);

  useEffect(() => {
    loadOrders(); return () => { generation.current++; };
  }, [loadOrders]);
  useEffect(() => { loadZones(); return () => { zoneGeneration.current++; }; }, [loadZones]);
  useEffect(() => { loadExistingRoute(); return () => { routeGeneration.current++; }; }, [loadExistingRoute]);

  const visibleOrders = orders.filter(o => `${o.order_number} ${o.customer_name} ${o.shipping_address_line1} ${o.shipping_postcode}`.toLowerCase().includes(search.toLowerCase()));
  const editable = !existingRouteId || !!existingRoute && ['draft', 'planned'].includes(existingRoute.status);
  const canSave = editable && !mutation.saving && selectedOrders.size > 0 && selectedOrders.size <= 50 && hasPermission(existingRouteId ? 'routes.update' : 'routes.create');
  const handleSelectAll = () => {
    if (visibleOrders.every(o => selectedOrders.has(o.id))) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(visibleOrders.slice(0, 50).map(o => o.id)));
    }
  };

  const handleToggleOrder = (orderId: number) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const handleCreateRoute = async () => {
    if (!selectedOrders.size || mutation.saving) return;
    const result = await mutation.save('/routes', 'POST', {
      name: newName.trim() || null, delivery_date: selectedDate, zone_ids: selectedZones.map(Number),
      estimated_start_time: newStartTime || null, order_ids: Array.from(selectedOrders),
    });
    if (result?.success) navigate(`/routes/${result.id}`);
  };
  const handleAddToExistingRoute = async () => {
    if (!existingRoute || !selectedOrders.size || mutation.saving) return;
    const result = await mutation.save(`/routes/${existingRoute.id}/stops`, 'POST', { order_ids: Array.from(selectedOrders), revision: existingRoute.revision });
    if (result?.success) navigate(`/routes/${existingRoute.id}`);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(`${dateStr.slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });
  };

  const formatCurrency = (value: number | null, currency: string | null) => {
    if (value === null || value === undefined) return '-';
    if (!currency || !/^[A-Z]{3}$/.test(currency)) return `${value.toFixed(2)} (currency not set)`;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(value);
  };


  // Group orders by postcode prefix for summary
  const postcodeGroups: Record<string, number> = {};
  orders.forEach(o => {
    const prefix = o.shipping_postcode?.split(' ')[0] || 'Unknown';
    postcodeGroups[prefix] = (postcodeGroups[prefix] || 0) + 1;
  });

  const tableRows = visibleOrders.map((order) => [
    <Checkbox
      label=""
      labelHidden
      checked={selectedOrders.has(order.id)}
      disabled={mutation.locked || !selectedOrders.has(order.id) && selectedOrders.size >= 50}
      onChange={() => handleToggleOrder(order.id)}
    />,
    <Button variant="plain" onClick={() => navigate(`/orders/${order.id}`)}>
      {order.order_number}
    </Button>,
    <Text as="span" variant="bodyMd" fontWeight="semibold">{order.customer_name}</Text>,
    <BlockStack gap="050">
      <Text as="span" variant="bodySm">{order.shipping_address_line1}</Text>
      <Text as="span" variant="bodySm" tone="subdued">{order.shipping_postcode}</Text>
    </BlockStack>,
    formatDate(order.delivery_date),
    order.zone_name || '-',
    formatCurrency(order.total_price, order.currency),
  ]);

  return (
    <Page
      title={existingRoute ? `Add to ${existingRoute.name || 'Route'}` : 'Build Route'}
      subtitle={`Select orders to ${existingRoute ? 'add to route' : 'create a new route'}`}
      backAction={{
        content: existingRoute ? 'Back to Route' : 'Routes',
        onAction: () => navigate(existingRoute ? `/routes/${existingRouteId}` : '/routes'),
      }}
      primaryAction={
        existingRouteId
          ? {
              icon: PlusIcon, content: `Add ${selectedOrders.size} Orders`,
              onAction: handleAddToExistingRoute,
              disabled: !canSave,
              loading: addingToRoute,
            }
          : {
              icon: PlusIcon, content: `Create Route (${selectedOrders.size})`,
              onAction: () => setCreateModalOpen(true),
              disabled: !canSave,
            }
      }
    >
      <Layout>
        {mutation.error && <Layout.Section><Banner tone="critical">{mutation.error} Retry the same save, or check the route before changing your selection.</Banner></Layout.Section>}
        {truncated && <Layout.Section><Banner tone="info">Showing the first 500 orders. Choose a date and zone to narrow the list.</Banner></Layout.Section>}
        {!!existingRouteId && !editable && <Layout.Section><Banner tone="warning">This route is unavailable or no longer open for editing.</Banner></Layout.Section>}
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {existingRoute && (
          <Layout.Section>
            <Banner tone="info">
              Adding orders to: <strong>{existingRoute.name || `Route ${existingRoute.id}`}</strong>
              {' - '}
              {existingRoute.total_stops} existing stops
            </Banner>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start" blockAlign="start">
              <div style={{ width: '160px' }}>
                <TextField
                  disabled={mutation.locked}
                  label="Delivery Date"
                  type="date"
                  value={selectedDate}
                  onChange={setSelectedDate}
                  autoComplete="off"
                  labelHidden
                />
              </div>
              <div style={{ width: '180px' }}>
                <ChoiceList title="Delivery zones" allowMultiple
                  disabled={mutation.locked || !!existingRouteId}
                  choices={zones.map(z => ({ label: z.name, value: String(z.id) }))}
                  selected={selectedZones} onChange={setSelectedZones} />
                <Text as="p" tone="subdued">{existingRouteId ? 'Only orders from this route’s zones are shown.' : 'Leave empty for all zones.'}</Text>
              </div>
              <div style={{ width: '200px' }}>
                <TextField
                  disabled={mutation.locked}
                  label="Search"
                  labelHidden
                  value={search}
                  onChange={setSearch}
                  autoComplete="off"
                  placeholder="Search orders..."
                  clearButton
                  onClearButtonClick={() => setSearch('')}
                />
              </div>
              <Button onClick={loadOrders} disabled={mutation.locked}>Refresh</Button>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Summary Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap>
            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Available Orders</Text>
                <Text as="p" variant="heading2xl">{orders.length}</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Selected</Text>
                <Text as="p" variant="heading2xl">{selectedOrders.size}</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="span" variant="bodySm" tone="subdued">Postcodes</Text>
                <InlineStack gap="200" wrap>
                  {Object.entries(postcodeGroups).slice(0, 6).map(([prefix, count]) => (
                    <Badge key={prefix}>{`${prefix}: ${count}`}</Badge>
                  ))}
                </InlineStack>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Orders List */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Orders for {selectedDate ? formatDate(selectedDate) : 'All Dates'}
                </Text>
                <Button onClick={handleSelectAll} disabled={mutation.locked || loading}>
                  {visibleOrders.every(o => selectedOrders.has(o.id)) ? 'Deselect All' : 'Select up to 50'}
                </Button>
              </InlineStack>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner />
                </div>
              ) : visibleOrders.length === 0 ? (
                <Banner tone="info">
                  No unassigned orders found for the selected filters.
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'numeric']}
                  headings={['', 'Order', 'Customer', 'Address', 'Date', 'Zone', 'Value']}
                  rows={tableRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create Route Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create Route"
        primaryAction={{
          icon: PlusIcon, content: 'Create Route',
          onAction: handleCreateRoute,
          loading: creating,
          disabled: !canSave,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setCreateModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {mutation.error && <Banner tone="critical">{mutation.error} Retry this save, or close the form and check the route list before starting another.</Banner>}
            <Text as="p">Choose a van and driver from the fleet booking panel after saving.</Text>
            <Banner tone="info">
              Creating route with {selectedOrders.size} orders for {formatDate(selectedDate)}
            </Banner>

            <TextField
              disabled={mutation.locked}
              label="Route Name"
              requiredIndicator value={newName}
              onChange={setNewName}
              autoComplete="off"
              placeholder="e.g., Morning Run - North"
              helpText="Optional name for easy identification"
            />

            <TextField
              disabled={mutation.locked}
              label="Start Time"
              type="time"
              value={newStartTime}
              onChange={setNewStartTime}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
